import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Phone, Mail, Calendar, TrendingUp, Users, Clock, Edit, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { getAgentDataById } from "../services/crmServices/userService";
import { getFullName, getInitials, getStatusName, getTimePasswed } from "../services/common/util";
import { useSelector } from "react-redux";
import { selectLeadStatuses } from "../store/slices/leadStatusSlice";

export function AgentProfile() {
    const { agentId } = useParams();
    const navigate = useNavigate();
    const [agentData, setAgentData] = useState(null);
    const leadStatuses = useSelector(selectLeadStatuses);
    const [agent, setAgent] = useState({
        id: agentId,
        name: `Agent ${agentId?.split("-")[1] || "1"}`,
        email: `agent${agentId?.split("-")[1] || "1"}@company.com`,
        phone: "+1 (555) 123-4567",
        position: "Sales Agent",
        desk: "Sales Desk A",
        joinDate: "2023-06-15",
        status: "Active",
        avatar: `A${agentId?.split("-")[1] || "1"}`,
        stats: {
            totalCalls: 1456,
            callsToday: 23,
            callsThisWeek: 156,
            callsThisMonth: 678,
            leadsAssigned: 45,
            leadsConverted: 12,
            conversionRate: 26.7,
            avgCallDuration: "8m 32s"
        },
        recentActivity: [
            { type: "call", description: "Called John Smith - Follow up", time: "2 hours ago" },
            { type: "lead", description: "New lead assigned - Alice Cooper", time: "4 hours ago" },
            { type: "conversion", description: "Converted lead - Mike Johnson", time: "1 day ago" }
        ]
    });

    const handleEdit = () => {
        toast.info("Edit agent profile - would open edit form");
    };

    const handleBack = () => {
        navigate("/staff");
    };

    useEffect(() => {
        if (agentId)
            getAgentDataById(agentId).then((res) => {
                if (res.status === 200) {
                    setAgentData(res.data);
                }
            });
    }, [agentId]);
    return (
        <div className="container mx-auto px-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Agent Profile</h1>
            </div>
            {agentData && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Agent Info Card */}
                    <Card className="lg:col-span-1">
                        <CardHeader className="text-center">
                            <Avatar className="w-24 h-24 mx-auto mb-4">
                                <AvatarFallback className="text-xl font-bold bg-blue-100 text-blue-600">
                                    {agentData?.firstName ? getInitials(getFullName(agentData)) : "U"}
                                </AvatarFallback>
                            </Avatar>
                            <CardTitle>{getFullName(agentData)}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center space-x-3">
                                <Mail className="h-4 w-4 text-gray-400" />
                                <span className="text-sm">{agentData.email}</span>
                            </div>
                            <div className="flex items-center space-x-3">
                                <Phone className="h-4 w-4 text-gray-400" />
                                <span className="text-sm">{agentData.phone || "-"}</span>
                            </div>
                            <div className="flex items-center space-x-3">
                                <Users className="h-4 w-4 text-gray-400" />
                                <span className="text-sm">{agentData.agentDesk?.name || "-"}</span>
                            </div>
                            <div className="flex items-center space-x-3">
                                <Calendar className="h-4 w-4 text-gray-400" />
                                <span className="text-sm">Joined {agentData.createdDate}</span>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Performance Stats */}
                    <div className="lg:col-span-2 space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center">
                                    <TrendingUp className="h-5 w-5 mr-2" />
                                    Performance Metrics
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-blue-600">
                                            {agentData.callCountToday ?? 0}
                                        </div>
                                        <div className="text-sm text-gray-600">Calls Today</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-green-600">
                                            {agentData.callCountWeek ?? 0}
                                        </div>
                                        <div className="text-sm text-gray-600">Calls This Week</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-purple-600">
                                            {agentData.callCountMonth ?? 0}
                                        </div>
                                        <div className="text-sm text-gray-600">Calls This Month</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-orange-600">
                                            {agentData.callCountTotal ?? 0}
                                        </div>
                                        <div className="text-sm text-gray-600">Total Calls</div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* My Lead Status Breakdown */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>My Lead Status Breakdown</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        {(() => {
                                            // If we have leadStatuses from Redux, use them to show all statuses
                                            if (leadStatuses && leadStatuses.length > 0) {
                                                const statusList = leadStatuses.map((statusOption) => {
                                                    const status = statusOption.value;
                                                    const count = agentData.leadStatusBreakdown?.[status] || 0;
                                                    return { statusOption, count };
                                                }).filter(({ count }) => count > 0); // Only show statuses with count > 0
                                                
                                                if (statusList.length === 0) {
                                                    return <div className="text-sm text-gray-500">No leads assigned or no leads with statuses</div>;
                                                }
                                                
                                                return statusList.map(({ statusOption, count }) => (
                                                    <div key={statusOption.value} className="flex justify-between items-center">
                                                        <span className="text-sm">{statusOption.label}</span>
                                                        <span
                                                            className="px-2 py-1 rounded text-sm font-medium"
                                                            style={{
                                                                backgroundColor: statusOption.bgColor || "#f3f4f6",
                                                                color: statusOption.color || "#1f2937"
                                                            }}
                                                        >
                                                            {count}
                                                        </span>
                                                    </div>
                                                ));
                                            }
                                            
                                            // Fallback: Show statuses from breakdown data if Redux statuses are not loaded
                                            if (agentData.leadStatusBreakdown && Object.keys(agentData.leadStatusBreakdown).length > 0) {
                                                const breakdownEntries = Object.entries(agentData.leadStatusBreakdown)
                                                    .filter(([status, count]) => count > 0);
                                                
                                                if (breakdownEntries.length === 0) {
                                                    return <div className="text-sm text-gray-500">No leads with statuses found</div>;
                                                }
                                                
                                                return breakdownEntries.map(([status, count]) => (
                                                    <div key={status} className="flex justify-between items-center">
                                                        <span className="text-sm capitalize">{status.replace(/_/g, " ")}</span>
                                                        <span className="px-2 py-1 rounded text-sm font-medium bg-gray-100 text-gray-800">
                                                            {count}
                                                        </span>
                                                    </div>
                                                ));
                                            }
                                            
                                            // No breakdown data at all
                                            return <div className="text-sm text-gray-500">No leads assigned to this agent</div>;
                                        })()}
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Lead Statistics */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>Lead Statistics</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm">Leads Assigned</span>
                                        <Badge variant="outline">{agentData.assignedLeadsCount ?? 0}</Badge>
                                    </div>
                                </CardContent>
                            </Card>

                        </div>
                        
                        {/* Recent Activity - Full Width */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center">
                                    <Clock className="h-5 w-5 mr-2" />
                                    Recent Activity
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                                        {(() => {
                                            // Combine calls and comments
                                            const activities = [];
                                            
                                            // Add all calls
                                            if (agentData.allCalls && agentData.allCalls.length > 0) {
                                                agentData.allCalls.forEach((call) => {
                                                    if (call.call && call.call.lead) {
                                                        activities.push({
                                                            type: "call",
                                                            lead: call.call.lead,
                                                            status: call.call.status,
                                                            createdAt: call.createdAt,
                                                            id: call._id
                                                        });
                                                    }
                                                });
                                            }
                                            
                                            // Add all comments
                                            if (agentData.allComments && agentData.allComments.length > 0) {
                                                agentData.allComments.forEach((comment) => {
                                                    if (comment.lead && comment.text) {
                                                        activities.push({
                                                            type: "comment",
                                                            lead: comment.lead,
                                                            author: comment.author,
                                                            affiliateName: comment.affiliateName, // Include affiliate name for display
                                                            text: comment.text,
                                                            createdAt: comment.createdAt,
                                                            id: comment._id
                                                        });
                                                    }
                                                });
                                            }
                                            
                                            // Sort by date (most recent first)
                                            activities.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                                            
                                            if (activities.length === 0) {
                                                return <div className="text-sm text-gray-500">No recent activity</div>;
                                            }
                                            
                                            return activities.map((activity) => (
                                                <div key={activity.id || Math.random()} className="flex items-start space-x-3 p-2 bg-gray-50 rounded">
                                                    <div className={`w-2 h-2 rounded-full mt-2 ${activity.type === "call" ? "bg-blue-500" : "bg-green-500"}`} />
                                                    <div className="flex-1">
                                                        {activity.type === "call" ? (
                                                            <>
                                                                <p className="text-sm font-medium">
                                                                    Called {getFullName(activity.lead)}
                                                                    {activity.status && ` - ${getStatusName(activity.status)}`}
                                                                </p>
                                                                <p className="text-xs text-gray-500">
                                                                    {getTimePasswed(activity.createdAt)}
                                                                </p>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <p className="text-sm font-medium">
                                                                    Comment on {getFullName(activity.lead)}
                                                                    {activity.affiliateName 
                                                                        ? ` by ${activity.affiliateName}`
                                                                        : activity.author && ` by ${getFullName(activity.author)}`}
                                                                </p>
                                                                <p className="text-xs text-gray-600 mt-1">{activity.text}</p>
                                                                <p className="text-xs text-gray-500 mt-1">
                                                                    {getTimePasswed(activity.createdAt)}
                                                                </p>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                </CardContent>
                            </Card>
                    </div>
                </div>
            )}
        </div>
    );
}
